import React, { useMemo } from "react";
import { Box, Paper, SvgIcon, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import { useLocation, useNavigate } from "react-router-dom";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";
import {
  getBuyerNdas,
  BUYER_NOTIFICATIONS,
  BUYER_TODOS,
  DEMO_BUYER_PROFILE,
  formatVndCompact,
  rankBuyerCases,
  readNdaState,
} from "./buyerWorkspaceData";

function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function BellIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M6.5 10a5.5 5.5 0 0 1 11 0v3.2l1.7 2.8H4.8l1.7-2.8V10Zm3.6 8a2.1 2.1 0 0 0 3.8 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function useBuyerDashboardMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([720, 1000]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([18, 28]).clamp(true)(height)),
      actionHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([330, 400]).clamp(true)(height)),
      kpiHeight: Math.round(d3.scaleLinear().domain([620, 1080]).range([90, 112]).clamp(true)(height)),
      kpiValueSize: Math.round(d3.scaleLinear().domain([720, 1000]).range([22, 29]).clamp(true)(width)),
      rowHeight: Math.round(d3.scaleLinear().domain([720, 1000]).range([58, 68]).clamp(true)(width)),
    };
  }, [shared]);
}

function BuyerDashboardContent(metrics) {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = location.state?.buyerInformation || DEMO_BUYER_PROFILE;
  const matches = useMemo(() => rankBuyerCases(profile), [profile]);
  const ndaState = useMemo(() => readNdaState(), []);
  const signedCount = getBuyerNdas().filter((agreement) => ndaState[agreement.id]?.status === "signed").length;
  const reachedOutCount = matches.filter((item) => item.reachedOut).length;
  const liveDealCount = matches.filter((item) => item.responded).length;
  const stackGap = Math.max(10, metrics.cardGap);
  const stackCardHeight = Math.floor((metrics.actionHeight - stackGap) / 2);
  const kpis = [
    { label: "Matching cases", value: String(matches.length), sub: "In your current market view" },
    { label: "NDAs signed", value: String(signedCount), sub: "Active CIM access" },
    { label: "Reached out", value: String(reachedOutCount), sub: "Cases contacted" },
    { label: "Live deals", value: String(liveDealCount), sub: "Cases with a response" },
  ];
  const openDeal = (caseId) => navigate(`/buyer/deals/${caseId}`, { state: { ...location.state, origin: "/buyer/dashboard" } });
  return (
    <Box>
      <Box>
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>
          Welcome back
        </Typography>
        <Typography component="h1" sx={{ mt: "5px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>
          Welcome back, Alex.
        </Typography>
        <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>
          {matches.length} opportunities match your current investment preferences.
        </Typography>
      </Box>

      <Box sx={{ mt: `${metrics.cardGap + 12}px`, display: "grid", gridTemplateColumns: metrics.compact ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: `${metrics.cardGap}px` }}>
        {kpis.map((item) => (
          <Paper key={item.label} elevation={0} sx={{ minHeight: `${metrics.kpiHeight}px`, p: `${metrics.cardPadding - 2}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>{item.label}</Typography>
            <Typography sx={{ mt: "5px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${metrics.kpiValueSize}px`, lineHeight: 1, color: ARAGON_COLORS.navy }}>{item.value}</Typography>
            <Typography sx={{ mt: "7px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, lineHeight: 1.3, color: ARAGON_COLORS.steel }}>{item.sub}</Typography>
          </Paper>
        ))}
      </Box>

      <Box sx={{ mt: `${metrics.cardGap}px`, display: "grid", gridTemplateColumns: metrics.tablet ? "1fr" : "minmax(0, 2.3fr) minmax(220px, 1fr)", gap: `${metrics.cardGap}px`, alignItems: "stretch" }}>
        <Paper elevation={0} sx={{ height: `${metrics.actionHeight}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.11)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
          <Box sx={{ px: `${metrics.cardPadding}px`, py: `${metrics.cardPadding - 2}px`, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "18px" }}>
            <Box>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>Market view</Typography>
              <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>Top matches for you</Typography>
            </Box>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>Updated today</Typography>
          </Box>
          <Box sx={{ borderTop: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` }}>
            {matches.slice(0, 4).map((item, index) => (
              <Box
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => openDeal(item.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") openDeal(item.id);
                }}
                sx={{ minHeight: `${metrics.rowHeight}px`, px: `${metrics.cardPadding}px`, display: "grid", gridTemplateColumns: metrics.compact ? "1fr auto" : "minmax(0, 1.55fr) minmax(88px, .65fr) minmax(88px, .65fr) auto", gap: "12px", alignItems: "center", borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}`, cursor: "pointer", transition: "background-color 140ms ease", "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.028) }, "&:focus-visible": { outline: `1px solid ${alpha(ARAGON_COLORS.navy, 0.22)}`, outlineOffset: "-1px" } }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>{item.id}</Typography>
                  <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 700, color: ARAGON_COLORS.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.subsector}</Typography>
                  <Typography sx={{ mt: "2px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>{item.location}</Typography>
                </Box>
                {!metrics.compact && (
                  <Box>
                    <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>Revenue</Typography>
                    <Typography sx={{ mt: "4px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${Math.max(11, metrics.bodySize)}px`, color: ARAGON_COLORS.navy }}>{formatVndCompact(item.revenue)}</Typography>
                  </Box>
                )}
                {!metrics.compact && (
                  <Box>
                    <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>EBITDA</Typography>
                    <Typography sx={{ mt: "4px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${Math.max(11, metrics.bodySize)}px`, color: ARAGON_COLORS.navy }}>{formatVndCompact(item.ebitda)}</Typography>
                  </Box>
                )}
                <ArrowIcon sx={{ fontSize: "17px", color: ARAGON_COLORS.steel }} />
              </Box>
            ))}
          </Box>
        </Paper>

        <Box sx={{ height: `${metrics.actionHeight}px`, display: "grid", gridTemplateRows: `repeat(2, ${stackCardHeight}px)`, gap: `${stackGap}px` }}>
          <Paper elevation={0} sx={{ p: `${metrics.cardPadding}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.11)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>Notifications</Typography>
              <BellIcon sx={{ fontSize: "16px", color: ARAGON_COLORS.steel }} />
            </Box>
            <Box sx={{ mt: "10px" }}>
              {BUYER_NOTIFICATIONS.map((item, index) => (
                <Box key={item.id} sx={{ py: "9px", borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}` }}>
                  <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, fontWeight: 650, lineHeight: 1.3, color: ARAGON_COLORS.navy }}>{item.title}</Typography>
                  <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, lineHeight: 1.3, color: ARAGON_COLORS.steel }}>{item.detail}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ p: `${metrics.cardPadding}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.11)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>To-dos</Typography>
              <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>{BUYER_TODOS.length}</Typography>
            </Box>
            <Box sx={{ mt: "8px" }}>
              {BUYER_TODOS.map((todo, index) => (
                <Box key={todo.id} sx={{ py: "8px", display: "grid", gridTemplateColumns: "11px 1fr", gap: "8px", borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}` }}>
                  <Box sx={{ mt: "3px", width: "9px", height: "9px", borderRadius: "50%", border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.28)}` }} />
                  <Box>
                    <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, fontWeight: 600, lineHeight: 1.3, color: ARAGON_COLORS.navy }}>{todo.title}</Typography>
                    <Typography sx={{ mt: "2px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.bodySize - 4)}px`, color: todo.due === "Today" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel }}>{todo.due}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

export default function BuyerDashboard() {
  const metrics = useBuyerDashboardMetrics();
  return (
    <LayoutMapping metrics={metrics} sidebar={<WorkspaceSidebar metrics={metrics} workspace="buyer" />}>
      <BuyerDashboardContent {...metrics} />
    </LayoutMapping>
  );
}
